import { useEffect, useState } from "react";
import { Button, Dropdown, Input, Popover, Select, Skeleton, Table, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CheckOutlined,
  DeleteOutlined,
  MinusCircleOutlined,
  MoreOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useRecoilValue } from "recoil";
import ColorPicker from "../../../../brand/ColorPicker";
import { ConfirmDeleteColorModal } from "../../../../brand/ConfirmDeleteColorModal";
import BrandKitApi, { BrandColor, ColorRole } from "../../../../../api/BrandKitApi";
import { BrandKitDto } from "../../../../../generated/dashboard-client";
import { memberState } from "../../../../../state/atoms";
import "./ColorsTab.scss";

const ROLES: ColorRole[] = ["PRIMARY", "SECONDARY", "TERTIARY", "NEUTRAL"];
const roleLabel = (role: ColorRole) => role.charAt(0) + role.slice(1).toLowerCase();
const roleSortIndex = (role?: ColorRole | null) => (role ? ROLES.indexOf(role) : ROLES.length);

export function ColorsTab({ selected }: { selected: BrandKitDto }) {
  const member = useRecoilValue(memberState);
  const canManageBrand =
    !!member && (member.owner || member.role === "Admin" || (member as any).manageBrand);

  const [colors, setColors] = useState<BrandColor[]>([]);
  const [loading, setLoading] = useState(false);
  const [menuOpenKey, setMenuOpenKey] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addPickerColor, setAddPickerColor] = useState("#ffffff");
  const [addName, setAddName] = useState("");
  const [addRole, setAddRole] = useState<ColorRole | undefined>(undefined);
  const [addTouched, setAddTouched] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [currentColor, setCurrentColor] = useState<BrandColor>({} as BrandColor);

  const [editingColor, setEditingColor] = useState<BrandColor | null>(null);
  const [editPickerColor, setEditPickerColor] = useState("");
  const [editColorName, setEditColorName] = useState("");

  useEffect(() => {
    if (!selected.id) return;
    setLoading(true);
    BrandKitApi.getColors(selected.id)
      .then((resp) => setColors(resp.data || []))
      .catch(() => message.error("Failed to load colors"))
      .finally(() => setLoading(false));
  }, [selected.id]);

  function reload() {
    if (selected.id) BrandKitApi.getColors(selected.id).then((resp) => setColors(resp.data || []));
  }

  function resetAddForm() {
    setAddPickerColor("#ffffff");
    setAddName("");
    setAddRole(undefined);
    setAddTouched(false);
  }

  function commitAddColor() {
    if (!selected.id || !addTouched) return;
    const code = addPickerColor;
    if (colors.some((c) => c.colorCode === code)) {
      message.error("That color is already in your library");
      return;
    }
    const name = addName.trim() || undefined;
    const role = addRole ?? null;
    const cleared = role ? colors.map((c) => (c.role === role ? { ...c, role: null } : c)) : colors;
    setColors([...cleared, { colorCode: code, name, role }]);
    BrandKitApi.addColor(selected.id, code, name, role)
      .then((resp) => setColors(resp.data || []))
      .catch(() => {
        message.error("Failed to add color");
        reload();
      });
  }

  function onSetRole(color: BrandColor, role: ColorRole | null) {
    if (!selected.id || !color.colorCode) return;
    setColors((prev) =>
      prev.map((c) => {
        if (c.colorCode === color.colorCode) return { ...c, role };
        if (role && c.role === role) return { ...c, role: null };
        return c;
      })
    );
    BrandKitApi.setColorRole(selected.id, color.colorCode, role)
      .then((resp) => setColors(resp.data || []))
      .catch(() => {
        message.error("Failed to update role");
        reload();
      });
  }

  function onDeleteColor(color: BrandColor) {
    setCurrentColor(color);
    setDeleteModalOpen(true);
  }

  function confirmDelete() {
    if (!selected.id) return;
    setDeleting(true);
    BrandKitApi.deleteColor(selected.id, currentColor.colorCode!)
      .then((resp) => {
        setColors(resp.data || []);
        setDeleteModalOpen(false);
      })
      .catch(() => message.error("Failed to delete color"))
      .finally(() => setDeleting(false));
  }

  function openEditColor(color: BrandColor) {
    setEditingColor(color);
    setEditPickerColor(color.colorCode || "#ffffff");
    setEditColorName(color.name || "");
  }

  function handleEditColorClose(open: boolean) {
    if (!open && editingColor) {
      const oldCode = editingColor.colorCode!;
      const newCode = editPickerColor;
      const newName = editColorName.trim() || undefined;
      const changed = oldCode !== newCode || (editingColor.name || "") !== (newName || "");
      if (changed && selected.id) {
        setColors((prev) =>
          prev.map((c) => (c.colorCode === oldCode ? { ...c, colorCode: newCode, name: newName } : c))
        );
        BrandKitApi.updateColor(selected.id, oldCode, newCode, newName)
          .then((resp) => setColors(resp.data || []))
          .catch(() => {
            message.error("Failed to update color");
            reload();
          });
      }
      setEditingColor(null);
    }
  }

  function roleMenu(color: BrandColor) {
    return (
      <div className="ops-menu colors-role-ops">
        <ul>
          {ROLES.map((r, i) => {
            const active = color.role === r;
            return (
              <li key={r} className={i === 0 ? "round-top" : ""}>
                <Button
                  type="link"
                  className="role-item"
                  onClick={() => {
                    if (!active) onSetRole(color, r);
                    setMenuOpenKey(null);
                  }}
                >
                  <span>{active ? roleLabel(r) : `Set as ${roleLabel(r)}`}</span>
                  {active && <CheckOutlined className="role-check" />}
                </Button>
              </li>
            );
          })}
          {color.role && (
            <li className="has-divider">
              <Button
                type="link"
                onClick={() => {
                  onSetRole(color, null);
                  setMenuOpenKey(null);
                }}
              >
                <MinusCircleOutlined /> Remove Role
              </Button>
            </li>
          )}
          <li className={color.role ? "" : "has-divider"}>
            <Button
              type="link"
              onClick={() => {
                onDeleteColor(color);
                setMenuOpenKey(null);
              }}
            >
              <DeleteOutlined /> Delete
            </Button>
          </li>
        </ul>
      </div>
    );
  }

  const columns: ColumnsType<BrandColor> = [
    {
      title: "Color",
      key: "color",
      sorter: (a, b) => (a.name || a.colorCode || "").localeCompare(b.name || b.colorCode || ""),
      render: (_, c) => (
        <div className="color-cell">
          <Popover
            placement="bottomLeft"
            trigger={canManageBrand ? "click" : []}
            open={editingColor?.colorCode === c.colorCode}
            onOpenChange={(open) => {
              if (open) openEditColor(c);
              else handleEditColorClose(false);
            }}
            content={
              <div className="brand-kit-color-picker">
                <ColorPicker onColorChange={setEditPickerColor} />
                <Input
                  className="brand-kit-color-name-input"
                  placeholder="Color name"
                  value={editColorName}
                  onChange={(e) => setEditColorName(e.target.value)}
                />
              </div>
            }
          >
            <div
              className="color-swatch"
              style={{ backgroundColor: c.colorCode, cursor: canManageBrand ? "pointer" : "default" }}
            />
          </Popover>
          <div className="color-meta">
            <span className="color-name">{c.name || "Untitled"}</span>
            <span className="color-hex">{c.colorCode}</span>
          </div>
        </div>
      ),
    },
    {
      title: "Role",
      key: "role",
      width: 180,
      sorter: (a, b) => roleSortIndex(a.role) - roleSortIndex(b.role),
      render: (_, c) =>
        c.role ? <span className="role-badge">{roleLabel(c.role)}</span> : <span className="muted">—</span>,
    },
    ...(canManageBrand
      ? [
          {
            title: "",
            key: "actions",
            width: 56,
            align: "right" as const,
            render: (_: any, c: BrandColor) => (
              <Dropdown
                trigger={["click"]}
                placement="bottomRight"
                overlay={roleMenu(c)}
                visible={menuOpenKey === c.colorCode}
                onVisibleChange={(v) => setMenuOpenKey(v ? c.colorCode || null : null)}
              >
                <Button type="text" icon={<MoreOutlined rotate={90} />} className="colors-tab-row-action" />
              </Dropdown>
            ),
          },
        ]
      : []),
  ];

  const addContent = (
    <div className="brand-kit-color-picker add-color-popover">
      <ColorPicker
        onColorChange={(v) => {
          setAddPickerColor(v);
          setAddTouched(true);
        }}
      />
      <Input
        className="brand-kit-color-name-input"
        placeholder="Color Name"
        value={addName}
        onChange={(e) => {
          setAddName(e.target.value);
          setAddTouched(true);
        }}
      />
      <div className="add-color-role">
        <div className="add-color-role-label">Role (optional)</div>
        <Select
          className="brand-kit-color-role-select"
          placeholder="Set as..."
          allowClear
          value={addRole}
          onChange={(v) => {
            setAddRole(v);
            setAddTouched(true);
          }}
          getPopupContainer={(trigger) => trigger.parentElement as HTMLElement}
          options={ROLES.map((r) => ({ value: r, label: roleLabel(r) }))}
        />
      </div>
    </div>
  );

  return (
    <div className="colors-tab">
      <div className="colors-tab-header">
        <div className="colors-tab-hint">
          Assign roles via the <span className="dots">•••</span> menu. Each role can only be assigned once.
        </div>
        {canManageBrand && (
          <Popover
            placement="bottomRight"
            trigger="click"
            open={addOpen}
            onOpenChange={(open) => {
              setAddOpen(open);
              if (!open) {
                commitAddColor();
                resetAddForm();
              }
            }}
            content={addContent}
          >
            <Button className="colors-new-color-btn" icon={<PlusOutlined />}>
              New Color
            </Button>
          </Popover>
        )}
      </div>

      {loading ? (
        <Skeleton active />
      ) : (
        <Table
          className="colors-tab-table"
          rowKey={(c) => c.colorCode || ""}
          columns={columns}
          dataSource={colors}
          pagination={false}
          showSorterTooltip={false}
        />
      )}

      <ConfirmDeleteColorModal
        isVisible={deleteModalOpen}
        isLoading={deleting}
        onCancel={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
